const UUID_RE =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const CANONICAL_MARKETING_RE = new RegExp(
  `(?:^|/)(?:rpc/)?public/entities/(${UUID_RE})/marketing/([^/?#]+)`,
);
const OBJECT_MARKETING_RE = new RegExp(
  `(?:^|/)entities/(${UUID_RE})/marketing/([^/?#]+)`,
);
const LEGACY_MARKETING_RE = new RegExp(
  `(?:^|/)marketing/(${UUID_RE})/([^/?#]+)`,
);

export type ThemeMarketingRef = {
  entityId: string;
  filename: string;
};

function decodeFilename(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Parse a stored customization/landing image URL into entity + filename.
 * Accepts Kennel-style proxy paths and absolute RGW/S3 object URLs.
 */
export function parseThemeMarketingRef(
  raw?: string | null,
): ThemeMarketingRef | null {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed || trimmed.startsWith("data:")) {
    return null;
  }

  const candidates = [CANONICAL_MARKETING_RE, OBJECT_MARKETING_RE, LEGACY_MARKETING_RE];
  for (const re of candidates) {
    const match = trimmed.match(re);
    if (!match?.[1] || !match[2]) {
      continue;
    }
    const filename = decodeFilename(match[2]);
    if (!filename || filename.includes("..")) {
      return null;
    }
    return { entityId: match[1], filename };
  }
  return null;
}

/**
 * Same-origin display URL for customization media.
 * Tenant ingress (weproduceanimation.com) only forwards /rpc to selah-api.
 */
export function publicMarketingImageSrc(entityId: string, filename: string): string {
  const id = entityId.trim();
  const name = filename.trim();
  if (!id || !name) {
    return "";
  }
  return `/rpc/public/entities/${encodeURIComponent(id)}/marketing/${encodeURIComponent(name)}`;
}

/** Rewrite stored theme image URLs so landing <img> tags hit the API proxy. */
export function resolveThemeMediaSrc(raw?: string | null): string {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) {
    return "";
  }
  const ref = parseThemeMarketingRef(trimmed);
  if (ref) {
    return publicMarketingImageSrc(ref.entityId, ref.filename);
  }
  return trimmed;
}
